import type { ApiFriendRequest, ApiUser, FriendRequestResult } from "@hive/sdk";
import { hive } from "./apiClient";

export type FriendshipStatus = "pending" | "accepted";

export interface FriendSummary {
	userId: string;
	handle: string;
	displayName: string | null;
	avatarUrl: string | null;
	bio: string;
	createdAt: string;
}

export interface FriendRequestSummary {
	id: string;
	userId: string;
	handle: string;
	displayName: string | null;
	avatarUrl: string | null;
	createdAt: string;
}

export interface OutgoingFriendRequestSummary extends FriendRequestSummary {}

function toFriendSummary(user: ApiUser): FriendSummary {
	return {
		userId: user.id,
		handle: user.handle,
		displayName: user.displayName,
		avatarUrl: user.avatarUrl,
		bio: user.bio ?? "",
		createdAt: user.createdAt,
	};
}

function toRequestSummary(request: ApiFriendRequest): FriendRequestSummary {
	return {
		id: request.id,
		userId: request.user.id,
		handle: request.user.handle,
		displayName: request.user.displayName,
		avatarUrl: request.user.avatarUrl,
		createdAt: request.createdAt,
	};
}

export async function listFriends(): Promise<FriendSummary[]> {
	const result = await hive.friends.list({ pageSize: 100 });
	return result.items.map(toFriendSummary);
}

export async function listBlockedUsers(): Promise<FriendSummary[]> {
	const result = await hive.me.blockedUsers({ pageSize: 100 });
	return result.items.map(toFriendSummary);
}

export async function listFriendRequests(): Promise<{
	incoming: FriendRequestSummary[];
	outgoing: OutgoingFriendRequestSummary[];
}> {
	const [incoming, outgoing] = await Promise.all([
		hive.friends.requests({ direction: "incoming", pageSize: 100 }),
		hive.friends.requests({ direction: "outgoing", pageSize: 100 }),
	]);
	return {
		incoming: incoming.items.map(toRequestSummary),
		outgoing: outgoing.items.map(toRequestSummary),
	};
}

export async function sendFriendRequest(handle: string): Promise<FriendRequestResult> {
	return hive.friends.request(handle.replace(/^@+/, ""));
}

export async function acceptFriendRequest(id: string) {
	return hive.friends.accept(id);
}

export async function declineFriendRequest(id: string) {
	return hive.friends.decline(id);
}

export async function removeFriend(handle: string): Promise<void> {
	await hive.friends.remove(handle);
}

export async function blockUser(handle: string): Promise<void> {
	await hive.users.block(handle.replace(/^@+/, ""));
}

export async function unblockUser(handle: string): Promise<void> {
	await hive.users.unblock(handle.replace(/^@+/, ""));
}
